import { useState, useEffect, useRef } from "react";
import "@/App.css";
import "@/i18n";
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import CookieBanner from "@/components/CookieBanner";
import InactivityWarningModal from "@/components/InactivityWarningModal";
import TaxResidencyModal from "@/components/TaxResidencyModal";
import LocationChangeModal from "@/components/LocationChangeModal";
import Footer from "@/components/Footer";
import Dashboard from "@/pages/Dashboard";
import TimeTracking from "@/pages/TimeTracking";
import Projects from "@/pages/Projects";
import Customers from "@/pages/Customers";
import Finances from "@/pages/Finances";
import Taxes from "@/pages/Taxes";
import Invoices from "@/pages/Invoices";
import Settings from "@/pages/Settings";
import Admin from "@/pages/Admin";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import Pricing from "@/pages/Pricing";
import UpgradeSuccess from "@/pages/UpgradeSuccess";
import Impressum from "@/pages/Impressum";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import TaxDocumentation from "@/pages/TaxDocumentation";
import Landing from "@/pages/Landing";
import NewLanding from "@/pages/NewLanding";
import { Clock, Briefcase, Users, Euro, Calculator, LayoutDashboard, Menu, X, FileText, Settings as SettingsIcon, ChevronLeft, ChevronRight, LogOut, CreditCard, Shield } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Navigation() {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const { t, i18n } = useTranslation();
  const navScrollRef = useRef(null);
  const { user, logout } = useAuth();
  
  // Helper function to translate plan names
  const getPlanDisplayName = (plan) => {
    if (!plan) return '';
    
    const planMap = {
      'free': t('pricing.plans.free.name') || 'Free',
      'basic': t('pricing.plans.basic.name') || 'Basic',
      'basic_monthly': t('pricing.plans.basic.name') || 'Basic',
      'basic_yearly': t('pricing.plans.basic.name') || 'Basic',
      'pro': t('pricing.plans.pro.name') || 'Pro',
      'pro_monthly': t('pricing.plans.pro.name') || 'Pro',
      'pro_yearly': t('pricing.plans.pro.name') || 'Pro',
      'lifetime': 'Lifetime'
    };
    
    return planMap[plan.toLowerCase()] || plan.replace('_', ' ');
  };
  
  const navItems = [
    { path: "/dashboard", label: t("nav.dashboard"), icon: LayoutDashboard, tourId: "dashboard" },
    { path: "/customers", label: t("nav.customers"), icon: Users, tourId: "customers" },
    { path: "/projects", label: t("nav.projects"), icon: Briefcase, tourId: "projects" },
    { path: "/time-tracking", label: t("nav.timeTracking"), icon: Clock, tourId: "time-tracking" },
    { path: "/finances", label: t("nav.finances"), icon: Euro, tourId: "finances" },
    { path: "/invoices", label: t("nav.invoices"), icon: FileText, tourId: "invoices" },
    { path: "/taxes", label: t("nav.taxes"), icon: Calculator, tourId: "taxes" },
    { path: "/settings", label: t("nav.settings"), icon: SettingsIcon, tourId: "settings" },
  ];

  // Add admin link if user is admin
  const ADMIN_EMAIL = "wilhelm.wagner.consulting@gmail.com";
  if (user && user.email === ADMIN_EMAIL) {
    navItems.push({ 
      path: "/admin", 
      label: "Admin", 
      icon: Shield, 
      tourId: "admin" 
    });
  }

  const checkScroll = () => {
    const element = navScrollRef.current;
    if (!element) return;
    const hasOverflow = element.scrollWidth > element.clientWidth;
    setShowLeftArrow(hasOverflow && element.scrollLeft > 5);
    setShowRightArrow(hasOverflow && element.scrollLeft < element.scrollWidth - element.clientWidth - 5);
  };

  useEffect(() => {
    checkScroll();
    const element = navScrollRef.current;
    if (element) {
      element.addEventListener('scroll', checkScroll);
    }
    window.addEventListener('resize', checkScroll);
    
    return () => {
      if (element) {
        element.removeEventListener('scroll', checkScroll);
      }
      window.removeEventListener('resize', checkScroll);
    };
  }, []);

  const scroll = (direction) => {
    if (navScrollRef.current) {
      const scrollAmount = 200;
      navScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-16 gap-4">
          {/* Logo */}
          <div className="flex items-center space-x-2 flex-shrink-0">
            <Calculator className="h-8 w-8 text-emerald-600" />
            <span className="text-xl font-bold text-gray-900">ivoliq</span>
          </div>
          
          {/* Desktop Navigation with Scroll */}
          <div className="hidden md:flex items-center flex-1 relative min-w-0">
            {showLeftArrow && (
              <button
                onClick={() => scroll('left')}
                className="absolute left-0 z-20 bg-white hover:bg-gray-50 shadow-lg rounded-full p-1.5 border border-gray-200"
                aria-label="Scroll left"
              >
                <ChevronLeft className="h-4 w-4 text-gray-700" />
              </button>
            )}
            
            <div 
              ref={navScrollRef}
              className="flex space-x-1 overflow-x-auto scrollbar-hide scroll-smooth w-full"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                    data-tour={item.tourId}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors whitespace-nowrap flex-shrink-0 ${
                      isActive
                        ? "bg-emerald-50 text-emerald-700"
                        : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                );
              })}
            </div>
            
            {showRightArrow && (
              <button
                onClick={() => scroll('right')}
                className="absolute right-0 z-20 bg-white hover:bg-gray-50 shadow-lg rounded-full p-1.5 border border-gray-200"
                aria-label="Scroll right"
              >
                <ChevronRight className="h-4 w-4 text-gray-700" />
              </button>
            )}
          </div>

          {/* User Info & Mobile menu button */}
          <div className="flex items-center space-x-2 flex-shrink-0">
            {/* User Info & Plan Badge */}
            {user && (
              <>
                <Link
                  to="/pricing"
                  className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors"
                >
                  <CreditCard className="h-4 w-4" />
                  <span className="text-sm font-semibold">{getPlanDisplayName(user.plan)}</span>
                </Link>
                <button
                  onClick={logout}
                  className="hidden md:flex items-center gap-2 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
                  title="Logout"
                >
                  <LogOut className="h-5 w-5" />
                </button>
              </>
            )}
            
            {/* Language switcher removed - DE only for Go-Live 1.0 (EN planned for v1.1) */}

            <button
              data-testid="mobile-menu-button"
              className="md:hidden p-2 rounded-lg hover:bg-gray-100"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                  data-tour={item.tourId}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? "bg-emerald-50 text-emerald-700"
                      : "text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
            
            {/* Mobile User Info */}
            {user && (
              <>
                <Link
                  to="/pricing"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-600 hover:bg-gray-50"
                >
                  <CreditCard className="h-5 w-5" />
                  <span className="font-medium">Plan: <span className="font-semibold">{getPlanDisplayName(user.plan)}</span></span>
                </Link>
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    logout();
                  }}
                  className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-600 hover:bg-gray-50"
                >
                  <LogOut className="h-5 w-5" />
                  <span className="font-medium">Logout</span>
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}

function AppModals() {
  const { 
    showTaxResidencyModal, 
    showLocationChangeModal,
    detectedCountry,
    locationCheckData,
    handleTaxResidencyConfirm,
    handleLocationUpdate,
    handleLocationChangeDismiss,
    user
  } = useAuth();

  return (
    <>
      <TaxResidencyModal
        isOpen={showTaxResidencyModal}
        onConfirm={handleTaxResidencyConfirm}
        detectedCountry={detectedCountry}
        userEmail={user?.email}
      />
      <LocationChangeModal
        isOpen={showLocationChangeModal}
        onClose={handleLocationChangeDismiss}
        onUpdate={handleLocationUpdate}
        currentCountry={locationCheckData?.current_tax_country}
        detectedCountry={locationCheckData?.detected_country}
      />
    </>
  );
}

// Smart root route component
function RootRoute() {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }
  
  // If authenticated, show app; otherwise show landing page
  return isAuthenticated ? <Navigate to="/dashboard" replace /> : <NewLanding />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <InactivityWarningModal />
        <AppModals />
        <div className="App min-h-screen bg-gray-50">
          <Routes>
            {/* Root route - smart redirect based on auth status */}
            <Route path="/" element={<RootRoute />} />
            
            {/* Public Routes */}
            <Route path="/landing" element={<Landing />} />
            <Route path="/new-landing" element={<NewLanding />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Login />} />
            <Route path="/register" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/upgrade-success" element={<UpgradeSuccess />} />
            <Route path="/impressum" element={<Impressum />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/tax-documentation" element={<TaxDocumentation />} />
            
            {/* Protected App Routes */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            } />
            <Route path="/customers" element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            } />
            <Route path="/projects" element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            } />
            <Route path="/time-tracking" element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            } />
            <Route path="/finances" element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            } />
            <Route path="/invoices" element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            } />
            <Route path="/taxes" element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            } />
            <Route path="/admin" element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            } />
          </Routes>
          <Toaster position="top-right" />
          <CookieBanner />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

function MainLayout() {
  const location = useLocation();
  
  // Determine which page component to render based on route
  let PageComponent = Dashboard; // default
  
  if (location.pathname === '/customers') PageComponent = Customers;
  else if (location.pathname === '/projects') PageComponent = Projects;
  else if (location.pathname === '/time-tracking') PageComponent = TimeTracking;
  else if (location.pathname === '/finances') PageComponent = Finances;
  else if (location.pathname === '/invoices') PageComponent = Invoices;
  else if (location.pathname === '/taxes') PageComponent = Taxes;
  else if (location.pathname === '/settings') PageComponent = Settings;
  else if (location.pathname === '/admin') PageComponent = Admin;
  
  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      <div className="flex-grow max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
        <PageComponent />
      </div>
      <Footer />
    </div>
  );
}

export default App;
export { API, toast };